import type { CSSProperties } from 'react'
import css from './LabelChip.module.css'
import LabelDot from './LabelDot'
import IconCrossSmall from '@/assets/icons/icon-cross-small.svg?react'

interface LabelChipProps {
    name: string
    color: string
    // Passed only where the chip acts as a filter (the project list). Without it the chip is
    // plain text — which is what the public share view and My tasks render.
    onClick?: () => void
    active?: boolean
    onRemove?: () => void
    testId?: string
}

const LabelChip = ({ name, color, onClick, active = false, onRemove, testId }: LabelChipProps) => {
    // The chip body is grey; --label-color only tints the dot (see LabelDot).
    const style = { '--label-color': color } as CSSProperties
    const className = [
        css.chip,
        onClick && css.clickable,
        active && css.active,
    ].filter(Boolean).join(' ')

    const body = (
        <>
            <LabelDot color={color} />
            <span className={css.name}>{name || 'Untitled'}</span>
        </>
    )

    if (onRemove) {
        return (
            <span className={`${className} ${css.removable}`} style={style} data-testid={testId}>
                {body}
                {/* An SVG cross, not a "×" character: the glyph's ink sits off-centre in its em box
                    (it's aligned to the font's math axis), so no amount of flex centring squares it
                    up — and the offset depends on the webfont. The icon is geometrically centred. */}
                <button className={css.remove} onClick={onRemove} aria-label={`Clear ${name} filter`}>
                    <IconCrossSmall />
                </button>
            </span>
        )
    }

    if (!onClick) {
        return (
            <span className={className} style={style} data-testid={testId}>
                {body}
            </span>
        )
    }

    const handleClick = (event: React.MouseEvent) => {
        // The chip sits inside the task title link — a bare click would navigate to the task
        // instead of filtering.
        event.preventDefault()
        event.stopPropagation()
        onClick()
    }

    return (
        <button className={className} style={style} onClick={handleClick} data-testid={testId}>
            {body}
        </button>
    )
}

export default LabelChip
